import {
  MDBBtn,
  MDBCol,
  MDBContainer,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
  MDBRow,
} from "mdbreact";
import React, { useEffect, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { AppContainer } from "../../../containers/App";
import { CampaignContainer } from "../../../containers/Campaign";
import { ErrorContainer } from "../../../containers/Error";
import { Recommend, StampCardContainer } from "../../../containers/StampCard";
import { Stamp, StampsContainer } from "../../../containers/Stamps";
import { UserContainer } from "../../../containers/User";
import TenantSelectElement from "../../elements/tenant-select-element/TenantSelectElement";
import TenantDescriptionModal from "../../modules/tenant-description-modal/TenantDescriptionModal";
import classes from "./TenantListPage.module.scss";
import { Config } from "../../../config/Config";

type TenantListPageProps = {} & RouteComponentProps<{
  category: string;
}>;

/**
 * テナント一覧画面コンポーネント
 */
const TenantListPage: React.FC<TenantListPageProps> = (props) => {
  /** エリア一覧モーダルの開閉状態 */
  const [isAreaListOpen, setIsAreaListOpen] = useState<boolean>(false);
  /** テナント詳細情報モーダルの状態管理 */
  const [isOpenModal, setIsOpenModal] = useState(false);
  /** タップ（クリック）されたリコメンド情報を管理するための state */
  const [openedStamp, setOpenedStamp] = useState<{
    category: string;
    recommend: Recommend;
  }>({
    category: "",
    recommend: {
      id: "",
      image: "",
      name: "",
    },
  });

  const appContainer = AppContainer.useContainer();
  const errorContainer = ErrorContainer.useContainer();
  const userContainer = UserContainer.useContainer();
  const campaignContainer = CampaignContainer.useContainer();
  const stampCardContainer = StampCardContainer.useContainer();
  const stampsContainer = StampsContainer.useContainer();
  const history = useHistory();

  const user = userContainer.user;
  const userId = user.id || "";

  const campaign = campaignContainer.campaign;
  const campaignId = Config.campaignId || "";
  const categories = campaign?.categories;

  const stampCard = stampCardContainer.stampCard;
  const recommends = stampCard?.recommends;

  useEffect(() => {
    if (!user.token) {
      // 未ログインの場合、ログインページに遷移
      history.push("/login");
    }

    if (!stampCard) {
      return;
    }
  }, [history, stampCard, campaignId, user.token]);

  // ルートパラメータからカテゴリを取得
  const category = props.match.params.category;

  const stamps = stampsContainer.stamps;
  if (
    stamps.category !== category &&
    !appContainer.context.loading &&
    recommends
  ) {
    // 対象カテゴリのスタンプ一覧の初期化
    appContainer.setLoading(true);
    stampsContainer
      .getStampList(campaignId, category, recommends[category])
      .then(() => appContainer.setLoading(false))
      .catch((error) => errorContainer.setDefaultError(userId, error));
  }

  // スタンプリストをエリアごとにまとめ上げ
  const reducer = (
    acc: {
      [key: string]: {
        area: string;
        stamps: {
          id: string;
          name: string;
          area: string;
          areaNumber: number;
          image: string;
          isChecked: boolean;
          description: string;
        }[];
      };
    },
    cur: Stamp
  ) => {
    cur.areaNumber.forEach((num, i) => {
      if (!acc[num]) {
        acc[num] = { area: cur.area[i], stamps: [] };
      }
      acc[num].stamps.push({
        id: cur.id,
        name: cur.name,
        area: cur.area[i],
        areaNumber: num,
        image: cur.image,
        description: cur.description || "",
        isChecked: false,
      });
    });

    return acc;
  };
  const stampGroups = stamps.stampList.reduce(reducer, {});

  /**
   * テナント詳細情報モーダルから選択されたらスタンプカードにセットする
   */
  const handleStampSelect = () => {
    stampCardContainer.recommendSelect(
      openedStamp.category,
      openedStamp.recommend
    );
  };

  /**
   * スタンプの選択処理
   * @param recommend リコメンド
   */
  const handleStampClicked = (recommendId: string) => {
    // テナント詳細情報モーダルを開く
    setIsOpenModal(true);
    const targetStamp = stamps.stampList.find((s) => s.id === recommendId);
    if (!targetStamp) {
      return;
    }
    // 選択されたスタンプ情報を state にセットする
    setOpenedStamp({
      category,
      recommend: {
        id: targetStamp.id,
        image: targetStamp.image,
        name: targetStamp.name,
        area: targetStamp.area,
        banner: targetStamp.banner,
        budget: targetStamp.budget,
        businessHours: targetStamp.businessHours,
        description: targetStamp.description,
        tel: targetStamp.tel,
        others: targetStamp.others,
        genre: targetStamp.genre,
      },
    });
  };

  /**
   * カテゴリの表示名を取得
   */
  const getCategoryName = () => {
    const categoryName = categories?.find(
      (c) => c.category === category
    )?.label;
    return categoryName;
  };

  return (
    <MDBContainer className={`${classes.container} d-flex flex-column`}>
      <div className={classes.pageHeader}>
        <div className={`text-center mt-3 mb-2 py-3 ${classes.pageTitle}`}>
          <div className={classes.titleText}>
            {getCategoryName()}関連のショップ一覧
          </div>
          <div>スタンプシートに登録するショップを１つ選んでください</div>
        </div>
        <div className="d-flex justify-content-between py-2">
          <MDBBtn
            className={`${classes.headerButton} px-2 py-1 px-sm-3 py-sm-2 m-0`}
            onClick={() => history.push(`/tenant-select`)}
          >
            <MDBIcon icon="angle-left mr-2"></MDBIcon>戻る
          </MDBBtn>
          <MDBBtn
            className={`${classes.headerButton} px-2 py-1 px-sm-3 py-sm-2 m-0`}
            onClick={() => {
              setIsAreaListOpen(true);
            }}
          >
            フロア一覧
          </MDBBtn>
        </div>
      </div>
      <div className={`${classes.areaListContainer} flex-grow-1`}>
        {Object.keys(stampGroups)
          .sort((areaNumA, areaNumB) => {
            return parseInt(areaNumA) - parseInt(areaNumB);
          })
          .map((areaNum: string, i: number) => {
            return (
              <MDBRow
                key={i}
                id={stampGroups[areaNum].area}
                className={`mb-4 px-2 ${classes.areaList}`}
              >
                <MDBCol
                  size="12"
                  className={`mt-2 mb-1 p-2 ${classes.listHeader}`}
                >
                  {stampGroups[areaNum].area}
                </MDBCol>
                {stampGroups[areaNum].stamps
                  ? stampGroups[areaNum].stamps.map((s, i) => {
                      return (
                        <MDBCol size="3" className="px-1 my-2" key={i}>
                          <div className="position-relative">
                            <TenantSelectElement
                              id={s.id}
                              name={s.name}
                              image={s.image}
                              isChecked={false}
                              onClick={(recommend) =>
                                handleStampClicked(recommend.id)
                              }
                            ></TenantSelectElement>
                          </div>
                        </MDBCol>
                      );
                    })
                  : null}
              </MDBRow>
            );
          })}
      </div>
      <TenantDescriptionModal
        onStampSelect={() => {
          handleStampSelect();
          setIsOpenModal(false);
          history.push(`/tenant-select`);
        }}
        isOpenModal={isOpenModal}
        onCloseButtonClick={() => {
          setIsOpenModal(false);
        }}
        tenantDescription={{
          image: openedStamp.recommend.banner || openedStamp.recommend.image,
          name: openedStamp.recommend.name,
          description: openedStamp.recommend.description || "",
          isRecommended: true,
          genre: openedStamp.recommend.genre,
          floor: openedStamp.recommend.area?.join("/"),
          businessHours: openedStamp.recommend.businessHours,
          tel: openedStamp.recommend.tel,
          budget: openedStamp.recommend.budget,
        }}
      />

      <MDBModal
        className={`${classes.areaSelectModal}`}
        isOpen={isAreaListOpen}
        toggle={() => {
          setIsAreaListOpen(false);
        }}
        position="right"
      >
        <MDBModalHeader>
          <span className={classes.title}>フロア一覧</span>
        </MDBModalHeader>
        <MDBModalBody className={classes.body}>
          {Object.keys(stampGroups)
            .sort((areaNumA, areaNumB) => {
              return parseInt(areaNumA) - parseInt(areaNumB);
            })
            .map((areaNum: string) => {
              return (
                <div
                  key={areaNum}
                  className={`position-relative text-center border-bottom py-2 ${classes.areaLink}`}
                >
                  <a
                    className="d-block"
                    href={`#${stampGroups[areaNum].area}`}
                    onClick={() => {
                      setIsAreaListOpen(false);
                    }}
                  >
                    <MDBIcon className={classes.icon} icon="angle-left" />
                    {stampGroups[areaNum].area}
                  </a>
                </div>
              );
            })}
        </MDBModalBody>
      </MDBModal>
    </MDBContainer>
  );
};

export default TenantListPage;
